import React, { useEffect, useState } from "react";
import { InputAdornment, TextField } from "@mui/material";
import {
  FieldInputProps,
  FieldMetaProps,
  useField,
  useFormikContext,
} from "formik";
import { useIntl } from "react-intl";
import { FormValues } from "./CreateFilterKpiForm";
import { FormatType, Kpi } from "./FilterType";
import { useRecoilValue } from "recoil";
import { Currency, currentUserCurrencyAtom } from "../../AuthProvider";
import CurrencyFormatInput from "../../Calendar/Popover/CurrencyFormatInput";
import { formInputsStyles } from "../../Configuration/styles";

export const KpiValueField = () => {
  const inputStyles = formInputsStyles();
  const {
    setFieldValue,
    values: { kpi },
  } = useFormikContext<FormValues>();
  const [params, setParams] = useState<any>({});
  const [field, meta] = useField("value");
  const intl = useIntl();
  const currency = useRecoilValue(currentUserCurrencyAtom);

  // TODO: change params when kpi changes
  useEffect(() => {
    if (kpi) {
      const params: any = {};
      if (
        [FormatType.PERCENT, FormatType.PROGRESS, FormatType.GAUGE].includes(
          kpi.format.type
        )
      ) {
        params["slotProps"] = {
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
        };
      } else if ([FormatType.CURRENCY].includes(kpi.format.type)) {
        params["InputProps"] = {
          inputComponent: CurrencyFormatInput as any,
        };
        params["onChange"] = (e: any) =>
          setFieldValue(field.name, e.target.value);
        params["slotProps"] = { htmlInput: { step: "0.01" } };
      }
      setParams(params);
    }
  }, [kpi, setFieldValue, currency, field.name]);

  return (
    <TextField
      className={inputStyles.classes.inputText}
      variant="outlined"
      {...field}
      error={!!(meta.touched && meta.error)}
      helperText={meta.touched && meta.error}
      label={intl.formatMessage({ id: "VALUE" })}
      {...params}
    />
  );
};

// bellow is specific code for business rules Kpi Form
// TODO: find a way to merge components

/**
 * Create input params for the kpi value field.
 */
export const kpiValueFieldSetParams = (kpi: Kpi, currency: Currency) => {
  const params: any = {};

  if (
    [FormatType.PERCENT, FormatType.PROGRESS, FormatType.GAUGE].includes(
      kpi.format.type
    )
  ) {
    params["slotProps"] = {
      input: {
        endAdornment: <InputAdornment position="end">%</InputAdornment>,
      },
    };
  } else if ([FormatType.CURRENCY].includes(kpi.format.type)) {
    params["slotProps"] = {
      input: {
        endAdornment: (
          <InputAdornment position="end">{currency.code}</InputAdornment>
        ),
      },
      htmlInput: { step: "0.01" },
    };
  }

  return params;
};

/**
 * KpiValueField component.
 */
export const KpiValueFieldComponent = (props: {
  field?: FieldInputProps<any>;
  params?: any;
  meta?: FieldMetaProps<any>;
  onChange?: (value: any | null) => void;
}): JSX.Element => {
  const { field, params, meta, onChange } = props;
  const intl = useIntl();
  const inputStyles = formInputsStyles();

  return (
    <TextField
      className={inputStyles.classes.inputText}
      variant="outlined"
      {...field}
      error={!!(meta && meta.touched && meta.error)}
      helperText={meta && meta.touched && meta.error}
      label={intl.formatMessage({ id: "VALUE" })}
      onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (onChange) {
          onChange(event.target.value);
        }
      }}
      {...params}
    />
  );
};

/**
 * KpiValueField component.
 */
export const KpiValueFieldBizRule = () => {
  const {
    setFieldValue,
    values: { kpi },
  } = useFormikContext<FormValues>();
  const [params, setParams] = useState<any>({});
  const [field, meta] = useField("value");
  const currency = useRecoilValue(currentUserCurrencyAtom);

  // TODO: change params when kpi changes
  useEffect(() => {
    if (kpi) {
      setParams(kpiValueFieldSetParams(kpi, currency));
    }
  }, [kpi, setFieldValue, currency]);

  return <KpiValueFieldComponent field={field} params={params} meta={meta} />;
};
